import { useEffect, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';
import { logToSentry } from '~/helpers/loggers.helper';
import { isInIframe } from '~/iframeMode';

const DEBOUNCE_MS = 10;

/*
 * Derives the parent window's origin for postMessage targeting.
 *
 * Same-origin parent (test harness, localhost): window.parent.location.origin
 * is accessible and returned directly.
 *
 * Cross-origin parent (production Webflow on www.<tld>): accessing
 * window.parent.location throws a SecurityError, so we fall back to
 * document.referrer which the browser sets to the embedding page's URL.
 *
 * Returns null when the parent origin cannot be determined (cross-origin +
 * empty referrer, e.g. strict Referrer-Policy). Callers must skip postMessage
 * in that case — posting to the iframe's own origin would be silently dropped
 * by the browser anyway, and '*' is prohibited (OWASP A01).
 */
const getParentOrigin = (): string | null => {
  try {
    return window.parent.location.origin;
  } catch {
    return document.referrer ? new URL(document.referrer).origin : null;
  }
};

/*
 * Reports the document's full content height to the parent window so the
 * embedding page (Webflow) can size the iframe to fit its content — removing
 * dead space and letting the host page scroll when content exceeds the
 * viewport (the agreed "widget-like" behaviour for the embedded card flow).
 *
 * We measure document.body.scrollHeight — unlike documentElement.scrollHeight
 * it is not clamped to the viewport height, so it reflects actual content
 * height once body has height: auto in iframe mode.
 *
 * Called at the CardApplication root; no-ops immediately when not in iframe
 * mode. Posts to a targetOrigin derived at runtime,
 * never '*', to avoid leaking messages to an untrusted parent (OWASP A01).
 */
const useIframeHeightSync = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!isInIframe) return;

    // Re-enable internal scrolling when height sync can't run, so the user is
    // never trapped on a non-scrollable page (iframe-mode CSS sets overflow-y:
    // hidden expecting the parent to grow the iframe instead).
    const enableScrollFallback = () => {
      document.body.style.overflowY = 'auto';
      document.documentElement.style.overflowY = 'auto';
    };

    // isInIframe is derived from ?iframe=true, which can also be opened in a
    // top-level tab (e.g. the harness "open" link). In that case there is no
    // embedding parent — posting would target ourselves — so bail out.
    if (window.parent === globalThis.window) {
      enableScrollFallback();
      return;
    }

    const parentOrigin = getParentOrigin();

    if (parentOrigin === null) {
      logToSentry(
        new Error('parent origin unknown, enabling scroll fallback'),
        'useIframeHeightSync'
      );
      // Can't determine parent origin — height sync is impossible.
      enableScrollFallback();
      return;
    }

    const postHeight = () => {
      const height = document.body.scrollHeight;
      globalThis.parent.postMessage(
        { type: 'qred:iframe-resize', height, iframeName: window.name },
        parentOrigin
      );
    };

    const debouncedPostHeight = () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(postHeight, DEBOUNCE_MS);
    };

    // Send initial height immediately on mount
    postHeight();

    // Observe the body so any content change (step navigation, expanding
    // sections, async-loaded content) re-triggers a height report.
    const observer = new ResizeObserver(debouncedPostHeight);
    observer.observe(document.body);

    return () => {
      observer.disconnect();
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);
};

export default useIframeHeightSync;
